import { useRef, useState, useCallback } from 'react';
import { useAgent, useDocuments } from '../../../context';
import { useStore } from '../../../../../../hooks';
import { createDocument, attachDocument, assignAgentToDocument, unassignAgentFromDocument, deleteDocument } from '../../../../../../services/document';
import { createFile } from '../../../../../../services/file';

/**
 * Hook para manejar subida de archivos al agente
 * 
 * Soporta múltiples archivos simultáneamente.
 * 
 * Flujo de subida por cada archivo:
 * 1. Agregar documento temporal con estado "loading" a la UI
 * 2. Crear documento en backend
 * 3. Asignar documento al agente
 * 4. Crear file (obtiene upload URL)
 * 5. Subir a S3
 * 6. Adjuntar documento
 * 7. Actualizar estado a "active"
 */

/**
 * Helper function to extract error from API response
 * Handles both HTTP errors (500+) and internal errors (HTTP 200 with status_code 40X)
 * 
 * @param {Object} responseData - The response data from the API
 * @param {Object} response - Optional axios response object (for HTTP status)
 * @param {string} defaultErrorMessage - Default error message if none found
 * @returns {Object|null} Error object with message and statusCode, or null if no error
 */
const extractApiError = (responseData, response = null, defaultErrorMessage = 'An error occurred.') => {
  if (!responseData) {
    return {
      message: 'Invalid response from server',
      statusCode: 500,
      responseData: null,
    };
  }

  // Check HTTP status first (for 500+ errors)
  const httpStatus = response?.status || 200;
  
  // Then check internal statusCode in body (for 400-499 errors that come with HTTP 200)
  const statusCode = responseData.status_code || responseData.statusCode || httpStatus;
  const msgCode = responseData.msg_code || responseData.msgCode || [];

  // Success: both HTTP status and internal statusCode are 2xx
  if (httpStatus >= 200 && httpStatus < 300 && statusCode >= 200 && statusCode < 300) {
    return null;
  }

  // Error: extract message from msgCode
  const errorMessage = msgCode.length > 0 
    ? (msgCode[0].description || msgCode[0].code || defaultErrorMessage)
    : defaultErrorMessage;

  return {
    message: errorMessage,
    statusCode: statusCode,
    responseData: responseData,
  };
};

/**
 * Helper function to validate API response and throw error if needed
 * 
 * @param {Object} responseData - The response data from the API
 * @param {Object} response - Optional axios response object
 * @param {string} defaultErrorMessage - Default error message
 * @returns {Object} Normalized response with status_code, body, etc.
 * @throws {Error} If response indicates an error
 */
const validateApiResponse = (responseData, response = null, defaultErrorMessage = 'An error occurred.') => {
  const error = extractApiError(responseData, response, defaultErrorMessage);
  if (error) {
    const apiError = new Error(error.message);
    apiError.response = {
      data: error.responseData,
      status: error.statusCode,
    };
    throw apiError;
  }

  // Return normalized response
  return {
    status_code: responseData.status_code || responseData.statusCode || 200,
    msg_code: responseData.msg_code || responseData.msgCode || [],
    body: responseData.body || null,
    meta: responseData.meta || null,
    request_id: responseData.request_id || responseData.requestId || null,
  };
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const useFileUpload = ({ onUploadStart } = {}) => {
  const fileInputRef = useRef(null);
  const [uploadingFiles, setUploadingFiles] = useState({}); // { [tempId]: { name, progress, error, step } }
  const [uploadErrors, setUploadErrors] = useState([]); // Array de errores para mostrar alertas
  
  // Contextos
  const { agent, agentId } = useAgent();
  const { addDocument, updateDocument, removeDocument } = useDocuments();
  
  // Stores (para auth)
  const { authStore } = useStore();

  // Generar ID temporal único
  const generateTempId = useCallback(() => {
    return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Actualizar estado de archivo en progreso
  const updateUploadingFile = useCallback((tempId, updates) => {
    setUploadingFiles((prev) => ({
      ...prev,
      [tempId]: { ...prev[tempId], ...updates },
    }));
  }, []);

  // Remover archivo de la lista de subida
  const removeUploadingFile = useCallback((tempId) => {
    setUploadingFiles((prev) => {
      const newState = { ...prev };
      delete newState[tempId];
      return newState;
    });
  }, []);

  // Limpiar errores
  const clearErrors = useCallback(() => {
    setUploadErrors([]);
  }, []);

  // Abre el file picker
  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Subir un archivo individual en background
  const uploadSingleFile = useCallback(async (file, tempId) => {
    const vaultUuid = agent?.vaultUuid;
    const userId = authStore?.userId;
    const tenantId = authStore?.selectedAccount?.id || '58145bef-4f71-468a-bded-1823d2a08f58';

    if (!vaultUuid) {
      const errorMessage = 'No vault available';
      console.error('No vaultUuid available');
      updateDocument(tempId, { 
        state: 'error',
        errorMessage: errorMessage,
        errorStep: 'Initialization',
      });
      updateUploadingFile(tempId, { 
        error: errorMessage,
        errorStep: 'Initialization',
        progress: 0,
      });
      setUploadErrors((prev) => [
        ...prev,
        { fileName: file.name, message: errorMessage, step: 'Initialization', tempId },
      ]);
      // Remove from documents list (Paso 1 rollback - nothing to revert)
      removeDocument(tempId);
      return;
    }

    if (!userId) {
      const errorMessage = 'Authentication required';
      console.error('No userId available');
      updateDocument(tempId, { 
        state: 'error',
        errorMessage: errorMessage,
        errorStep: 'Initialization',
      });
      updateUploadingFile(tempId, { 
        error: errorMessage,
        errorStep: 'Initialization',
        progress: 0,
      });
      setUploadErrors((prev) => [
        ...prev,
        { fileName: file.name, message: errorMessage, step: 'Initialization', tempId },
      ]);
      // Remove from documents list (Paso 1 rollback - nothing to revert)
      removeDocument(tempId);
      return;
    }

    // Variables para trackear recursos creados (para rollback)
    let documentUuid = null;
    let versionUuid = null;
    let fileId = null;
    let s3Key = null;

    try {
      // PASO 1: Crear documento en backend
      updateUploadingFile(tempId, { progress: 10, step: 'Creating document...' });
      
      const createDocumentBody = {
        name: file.name,
        vault_uuid: vaultUuid,
        created_by: userId,
        owner: userId,
        tenant_id: tenantId,
        type: file.type || 'application/octet-stream',
        url: '',
        metadata: {
          source: 'file_upload',
          version: '1.0',
        },
      };

      const createDocumentResponse = await createDocument(createDocumentBody);
      
      // Validate response - throws error if status_code is not 2xx
      const normalizedResponse = validateApiResponse(
        createDocumentResponse.data,
        createDocumentResponse,
        'Failed to create document'
      );

      const { body } = normalizedResponse;
      documentUuid = body?.document?.document_uuid;
      versionUuid = body?.version?.doc_version_uuid;

      if (!documentUuid) {
        throw new Error('Failed to create document: No document_uuid returned');
      }

      if (!versionUuid) {
        throw new Error('Failed to create document: No doc_version_uuid returned');
      }

      updateUploadingFile(tempId, { progress: 25, step: 'Assigning to agent...' });

      // PASO 2: Asignar documento al agente
      const assignBody = {
        document_uuid: documentUuid,
        agent_uuid: agentId,
        expires_at: '2026-12-31 23:59:59',
        owner: userId,
        created_by: userId,
        granted_by: userId,
      };

      await assignAgentToDocument(assignBody);
      updateUploadingFile(tempId, { progress: 40, step: 'Preparing upload...' });

      // PASO 3: Crear file para obtener URL de subida
      const createFileBody = {
        name: file.name,
        folder_id: `folder-${Date.now()}`,
        tenant_id: tenantId,
        user_id: userId,
        file_type: file.type || 'application/octet-stream',
        status: 'active',
        vault_uuid: vaultUuid,
        document_uuid: documentUuid,
        version_uuid: versionUuid,
      };

      const createFileResponse = await createFile(createFileBody);
      
      // Validate response - throws error if status_code is not 2xx
      const normalizedFileResponse = validateApiResponse(
        createFileResponse.data || createFileResponse,
        createFileResponse,
        'Failed to create file'
      );

      const fileData = normalizedFileResponse.body || createFileResponse.data || createFileResponse;
      const uploadUrl = fileData?.uploadUrl || fileData?.body?.uploadUrl;
      fileId = fileData?.id || fileData?.file_id || fileData?.body?.id;
      s3Key = fileData?.fileKey || fileData?.file_key || fileData?.s3_key || fileData?.body?.fileKey;

      if (!uploadUrl) {
        throw new Error('Failed to get upload URL: No URL received from server');
      }

      updateUploadingFile(tempId, { progress: 55, step: 'Uploading to storage...' });

      // PASO 4: Subir archivo a S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload to storage failed: HTTP ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      updateUploadingFile(tempId, { progress: 80, step: 'Attaching document...' });

      // PASO 5: Adjuntar documento
      const attachBody = {
        s3_key: s3Key,
        documentUuid: documentUuid,
        tenantId: tenantId,
      };

      await attachDocument(attachBody);
      updateUploadingFile(tempId, { progress: 100, step: 'Completed' });

      // PASO 6: Actualizar documento en UI con datos reales
      updateDocument(tempId, {
        uuid: documentUuid,
        name: file.name,
        state: 'active',
        type: file.type,
        created_at: new Date().toISOString(),
      });

      console.log('File uploaded successfully:', file.name);

      // Limpiar de la lista de subida después de un momento
      setTimeout(() => {
        removeUploadingFile(tempId);
      }, 2000);

    } catch (error) {
      console.error('Error uploading file:', file.name, error);
      
      // Determinar mensaje de error específico y paso donde falló
      let errorMessage = 'Upload failed';
      let errorStep = 'Unknown step';
      
      // Extract error from response if available
      const apiError = extractApiError(
        error.response?.data,
        error.response,
        error.message || 'Upload failed'
      );
      
      if (apiError) {
        errorMessage = apiError.message;
        // Determinar el paso basado en el statusCode o el mensaje
        if (apiError.statusCode === 409) {
          errorStep = 'Document creation'; // DOC_DUPLICATE u otros errores 409
        } else if (error.message?.includes('create file') || error.message?.includes('Failed to create file')) {
          errorStep = 'File creation';
        } else {
          errorStep = 'Upload process';
        }
      } else {
        const errorMsg = error.message || error.toString() || 'Upload failed';
        
        if (errorMsg.includes('create document') || errorMsg.includes('Failed to create document')) {
          errorMessage = `Failed to create document: ${errorMsg}`;
          errorStep = 'Document creation';
        } else if (errorMsg.includes('create file') || errorMsg.includes('Failed to create file')) {
          errorMessage = `Failed to create file: ${errorMsg}`;
          errorStep = 'File creation';
        } else if (errorMsg.includes('assign') || errorMsg.includes('Assigning')) {
          errorMessage = `Failed to assign document to agent: ${errorMsg}`;
          errorStep = 'Agent assignment';
        } else if (errorMsg.includes('upload URL') || errorMsg.includes('Failed to get upload URL')) {
          errorMessage = `Failed to get upload URL: ${errorMsg}`;
          errorStep = 'Upload preparation';
        } else if (errorMsg.includes('Upload to storage') || errorMsg.includes('storage failed')) {
          errorMessage = `Failed to upload to storage: ${errorMsg}`;
          errorStep = 'Storage upload';
        } else if (errorMsg.includes('attach') || errorMsg.includes('Attaching')) {
          errorMessage = `Failed to attach document: ${errorMsg}`;
          errorStep = 'Document attachment';
        } else {
          errorMessage = errorMsg;
          errorStep = 'Upload process';
        }
      }

      // ROLLBACK según el paso donde falló
      if (errorStep === 'Document creation' || errorStep === 'Initialization') {
        // PASO 1 falló: No hay nada que revertir, solo quitar de la lista
        removeDocument(tempId);
      } else if (errorStep === 'File creation' && documentUuid) {
        // PASO 3 falló: Revertir en orden
        try {
          // 1. Desasignar agente del documento
          await unassignAgentFromDocument(documentUuid, agentId, tenantId);
          console.log('Rollback: Agent unassigned from document:', documentUuid);
        } catch (rollbackError) {
          console.error('Rollback error (unassign):', rollbackError);
        }

        try {
          // 2. Eliminar el documento
          await deleteDocument({ document_uuid: documentUuid });
          console.log('Rollback: Document deleted:', documentUuid);
        } catch (rollbackError) {
          console.error('Rollback error (delete document):', rollbackError);
        }

        // Quitar de la lista de documentos
        removeDocument(tempId);
      } else if (documentUuid) {
        // Otros pasos fallaron después del paso 1: también hacer rollback
        try {
          await unassignAgentFromDocument(documentUuid, agentId, tenantId);
          await deleteDocument({ document_uuid: documentUuid });
          removeDocument(tempId);
        } catch (rollbackError) {
          console.error('Rollback error:', rollbackError);
          // Aún así quitar de la lista
          removeDocument(tempId);
        }
      } else {
        // Si no hay documentUuid, solo quitar de la lista
        removeDocument(tempId);
      }
      
      // Actualizar documento con error detallado
      updateDocument(tempId, { 
        state: 'error',
        errorMessage: errorMessage,
        errorStep: errorStep,
      });
      
      // Actualizar estado de subida
      updateUploadingFile(tempId, { 
        error: errorMessage,
        errorStep: errorStep,
        progress: 0,
      });
      
      // Agregar a lista de errores para mostrar alerta
      setUploadErrors((prev) => [
        ...prev,
        { 
          fileName: file.name,
          message: errorMessage,
          step: errorStep,
          tempId,
        }
      ]);
    }
  }, [agent, agentId, authStore, updateDocument, updateUploadingFile, removeUploadingFile, removeDocument]);

  // Maneja la selección de archivos (múltiples)
  const handleFileSelect = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    // Cambiar a tab de Documents cuando se seleccionan archivos
    if (onUploadStart && files.length > 0) {
      onUploadStart();
    }

    // Procesar cada archivo
    for (const file of files) {
      // Validar tamaño
      if (file.size > MAX_FILE_SIZE) {
        console.error('File too large:', file.name);
        continue;
      }

      // Generar ID temporal
      const tempId = generateTempId();

      // 1. Agregar documento temporal con estado "loading" a la UI
      const tempDoc = {
        uuid: tempId,
        name: file.name,
        state: 'loading',
        type: file.type,
        created_at: new Date().toISOString(),
      };
      
      addDocument(tempDoc);
      
      // Registrar en estado de subida
      setUploadingFiles((prev) => ({
        ...prev,
        [tempId]: { name: file.name, progress: 0, error: null },
      }));

      // 2. Subir en background (no await para permitir múltiples simultáneos)
      uploadSingleFile(file, tempId);
    }

    // Limpiar input para permitir seleccionar el mismo archivo de nuevo
    event.target.value = '';
  }, [addDocument, generateTempId, uploadSingleFile, onUploadStart]);

  // Reintentar subida de un archivo con error
  const retryUpload = useCallback((tempId) => {
    // TODO: Implementar reintento
    console.log('Retry upload:', tempId);
  }, []);

  // Cancelar subida
  const cancelUpload = useCallback((tempId) => {
    removeDocument(tempId);
    removeUploadingFile(tempId);
  }, [removeDocument, removeUploadingFile]);

  // Estado derivado
  // ✅ isUploading solo cuenta archivos que realmente se están subiendo (sin error)
  const isUploading = Object.values(uploadingFiles).some(
    (file) => !file.error && (file.progress > 0 && file.progress < 100)
  );
  const uploadingCount = Object.values(uploadingFiles).filter(
    (file) => !file.error && (file.progress > 0 && file.progress < 100)
  ).length;

  return {
    // Refs
    fileInputRef,
    
    // Estado
    uploadingFiles,
    isUploading,
    uploadingCount,
    uploadErrors, // Errores para mostrar alertas
    
    // Acciones
    openFilePicker,
    handleFileSelect,
    retryUpload,
    cancelUpload,
    clearErrors, // Limpiar errores
  };
};

export default useFileUpload;

